import useToggle from '../useToggle';
import history from '../../helpers/history';
import { fetchLoansApi } from '../../apis/loan';
import { Loan, LoanStatus } from '../../interfaces';
import { Pagination } from '../../interfaces/common';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getLoanStatusIdFromLoanStatusName, getParameterByName } from '../../helpers';
import { useErrorHandler } from 'react-error-boundary';
import { AxiosError } from 'axios';

export const useFetchLoans = () => {
  // toggle the loan filter modal
  const { show: showForm, toggle: toggleForm } = useToggle();

  // get the loan_types from the url query parameters
  const [loanTypes, setLoanTypes] = useState(getParameterByName('loan_types'));
  const [countrySelection, setCountrySelection] = useState(getParameterByName('countries'));

  const page = getParameterByName('page') ? parseInt(getParameterByName('page')!) : 1;

  const [pagination, setPagination] = useState<Pagination>();

  const [openLoans, setOpenLoans] = useState<Loan[] | null>(null);
  const [underwritingLoans, setUnderwritingLoans] = useState<Loan[] | null>(null);
  const [activeLoans, setActiveLoans] = useState<Loan[] | null>(null);
  const [closedLoans, setClosedLoans] = useState<Loan[] | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error|null>(null);
  useErrorHandler(error)

  const { menu } = useParams<{ menu: string }>();
  const { pathname } = useLocation<{ pathname: string }>();

  const makeActive = (path: string) => {
    const splitLocation = pathname.split('/');

    if (splitLocation[2]) {
      return splitLocation[2] === path ? 'active' : 'false';
    }
    return '';
  };

  const updateLoanTypeValues = (values: string) => setLoanTypes(values);
  const updateCountryValues = (values: string) => setCountrySelection(values);

  const pageQuery = (page: number = 1) => {
    let query = `?page=${page}`;
    if (loanTypes) {
      query += `&loan_types=${loanTypes}`;
    }
    if (countrySelection) {
      query += `&countries=${countrySelection}`;
    }
    return query;
  };

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    setError(null)
    try {
      const status = getLoanStatusIdFromLoanStatusName(menu);
      const response = await fetchLoansApi({
        page,
        types: loanTypes,
        status,
        countries: countrySelection,
      });
      const paginatedLoans: Pagination = response.data;
      setPagination(paginatedLoans);
      const loans: Loan[] = paginatedLoans.data;

      switch (menu) {
        case LoanStatus.OPEN:
          setOpenLoans(loans);
          break;
        case LoanStatus.UNDERWRITING:
          setUnderwritingLoans(loans);
          break;
        case LoanStatus.ONGOING:
          setActiveLoans(loans);
          break;
        default:
          setClosedLoans(loans);
          break;
      }
      setLoading(false);
    } catch (error) {
      const axiosError = error as AxiosError
      setError(axiosError)
      setLoading(false);
    }
  }, [menu, page, loanTypes, countrySelection])

  const handleFilterLoan = () => {
    history.push(`/loans/${menu}${pageQuery()}`);
    fetchLoans()
    toggleForm();
  };

  useEffect(() => {
    if (!menu) {
      history.push('/loans/open?page=1');
    } else {
      fetchLoans();
    }
  }, [menu, page]);

  return {
    error,
    showForm,
    loanTypes,
    countrySelection,
    page,
    menu,
    loading,
    openLoans,
    underwritingLoans,
    activeLoans,
    closedLoans,
    pagination,
    pageQuery,
    updateLoanTypeValues,
    updateCountryValues,
    handleFilterLoan,
    toggleForm,
    makeActive,
    fetchLoans
  };
};
